import { type ReactNode, useMemo, useRef, useState } from 'react';
import { Badge } from '../ui';

const LONG_PRESS_MS = 700;

function hasSelectionModifier(event: { shiftKey?: boolean; ctrlKey?: boolean; metaKey?: boolean }) {
  return Boolean(event.shiftKey || event.ctrlKey || event.metaKey);
}

type StudentIdentity = {
  id: string;
  firstName: string;
  lastName?: string | null;
};

type StudentSeatCardProps = {
  student: StudentIdentity;
  photoUrl: string;
  subtitle?: string;
  badgeCount?: number;
  badgeTitle?: string;
  accent?: 'none' | 'needs_help' | 'observation' | 'away';
  compact?: boolean;
  disabled?: boolean;
  selected?: boolean;
  actions?: ReactNode;
  onAward?: () => void;
  onSelect?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onOpenMenu?: (coords: { clientX: number; clientY: number }) => void;
};

export default function StudentSeatCard({
  student,
  photoUrl,
  subtitle,
  badgeCount = 0,
  badgeTitle,
  accent = 'none',
  compact = false,
  disabled = false,
  selected = false,
  actions,
  onAward,
  onSelect,
  onOpenMenu
}: StudentSeatCardProps) {
  const [imageFailed, setImageFailed] = useState(false);
  const longPressTimerRef = useRef<number | null>(null);
  const longPressTriggeredRef = useRef(false);
  const suppressNextModifierClickRef = useRef(false);

  const accentClass = useMemo(() => {
    if (selected) return 'ring-2 ring-primary-500 ring-offset-1 dark:ring-primary-400';
    if (accent === 'needs_help') return 'ring-2 ring-rose-500 dark:ring-rose-400';
    if (accent === 'observation') return 'ring-2 ring-amber-500 dark:ring-amber-400';
    if (accent === 'away') return 'ring-2 ring-amber-500 ring-offset-1 dark:ring-amber-400';
    return 'ring-0';
  }, [accent, selected]);

  const clearLongPressTimer = () => {
    if (longPressTimerRef.current != null) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const openMenu = (clientX: number, clientY: number) => {
    onOpenMenu?.({ clientX, clientY });
  };

  return (
    <button
      type="button"
      disabled={disabled}
      onContextMenu={(event) => {
        event.preventDefault();
        if (hasSelectionModifier(event)) {
          suppressNextModifierClickRef.current = true;
          window.setTimeout(() => {
            suppressNextModifierClickRef.current = false;
          }, 0);
          onSelect?.(event as React.MouseEvent<HTMLButtonElement>);
          return;
        }
        if (!onOpenMenu) return;
        openMenu(event.clientX, event.clientY);
      }}
      onPointerDown={(event) => {
        if (disabled || event.button !== 0) return;
        if (hasSelectionModifier(event)) {
          clearLongPressTimer();
          longPressTriggeredRef.current = false;
          return;
        }
        if (!onOpenMenu) return;
        longPressTriggeredRef.current = false;
        clearLongPressTimer();
        longPressTimerRef.current = window.setTimeout(() => {
          longPressTriggeredRef.current = true;
          openMenu(event.clientX, event.clientY);
        }, LONG_PRESS_MS);
      }}
      onPointerUp={clearLongPressTimer}
      onPointerLeave={clearLongPressTimer}
      onPointerCancel={clearLongPressTimer}
      onClick={(event) => {
        if (disabled || longPressTriggeredRef.current) {
          longPressTriggeredRef.current = false;
          return;
        }
        if (event.shiftKey || event.ctrlKey || event.metaKey) {
          clearLongPressTimer();
          longPressTriggeredRef.current = false;
          if (suppressNextModifierClickRef.current) {
            suppressNextModifierClickRef.current = false;
            return;
          }
          onSelect?.(event);
          return;
        }
        onAward?.();
      }}
      className={`group relative aspect-square w-full overflow-hidden rounded-xl border border-gray-200 bg-gray-100 text-left transition hover:border-primary-400 hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-primary-500 dark:hover:shadow-black/30 ${accentClass}`}
    >
      {!imageFailed ? (
        <img
          src={photoUrl}
          alt={`${student.firstName} ${student.lastName || ''}`}
          className="h-full w-full object-cover"
          onError={() => setImageFailed(true)}
          draggable={false}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-gray-300 text-sm font-semibold text-gray-700 dark:bg-gray-700 dark:text-gray-200">
          {student.firstName.slice(0, 1)}
          {(student.lastName || '').slice(0, 1)}
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 bg-black/60 px-2 py-1.5">
        <div className={`${compact ? 'text-xs' : 'text-sm'} truncate font-semibold text-white`}>
          {student.firstName} {student.lastName || ''}
        </div>
        {subtitle ? <div className="truncate text-[11px] text-white/80">{subtitle}</div> : null}
      </div>
      <div className="absolute left-1.5 top-1.5">
        <Badge
          variant="neutral"
          title={badgeTitle}
          className="border border-white/60 bg-white/95 text-[11px] text-gray-700 dark:border-gray-600 dark:bg-gray-900/90 dark:text-gray-200"
        >
          {badgeCount}
        </Badge>
      </div>
      {actions ? (
        <div
          className="absolute right-1.5 top-1.5 z-10"
          onClick={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
        >
          {actions}
        </div>
      ) : null}
      {accent === 'away' ? (
        <div className="absolute right-1.5 top-1.5 rounded bg-amber-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
          weg
        </div>
      ) : null}
    </button>
  );
}
